import { useState } from 'react';
import type { CashFlowEvent } from '../../services/cashFlowCalculations';
import { formatCurrency, formatDate } from '../../services/cashFlowCalculations';

interface CashFlowTableProps {
  events: CashFlowEvent[];
}

const CATEGORY_LABELS: Record<string, string> = {
  preventivo: 'Preventivo',
  finanziamento: 'Finanziamento',
  notaio: 'Notaio/Tasse',
  costo_extra: 'Costo Extra',
  rata: 'Rata',
  altro: 'Altro'
};

const CATEGORY_COLORS: Record<string, string> = {
  preventivo: 'bg-blue-100 text-blue-800',
  finanziamento: 'bg-green-100 text-green-800',
  notaio: 'bg-purple-100 text-purple-800',
  costo_extra: 'bg-orange-100 text-orange-800',
  rata: 'bg-gray-100 text-gray-800',
  altro: 'bg-gray-100 text-gray-800'
};

export function CashFlowTable({ events }: CashFlowTableProps) {
  const [filter, setFilter] = useState<string>('all');
  const [showDetails, setShowDetails] = useState(false);

  // Filter events
  const filteredEvents = filter === 'all'
    ? events
    : events.filter(e => e.category === filter);

  // Limit display if not showing details
  const displayEvents = showDetails ? filteredEvents : filteredEvents.slice(0, 10);

  // Unique categories for filter
  const categories = [...new Set(events.map(e => e.category))];

  if (events.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-gray-500">Nessun movimento registrato</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header with filters */}
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">
          Dettaglio Movimenti ({filteredEvents.length})
        </h3>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">Tutte le categorie</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Data</th>
              <th className="px-4 py-3 text-left">Descrizione</th>
              <th className="px-4 py-3 text-left">Categoria</th>
              <th className="px-4 py-3 text-right">Importo</th>
              <th className="px-4 py-3 text-right">Saldo</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {displayEvents.map((event, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                  {formatDate(event.date)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {event.description}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${CATEGORY_COLORS[event.category]}`}>
                    {CATEGORY_LABELS[event.category]}
                  </span>
                </td>
                <td className={`px-4 py-3 text-sm text-right font-medium whitespace-nowrap ${
                  event.amount >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {event.amount >= 0 ? '+' : ''}{formatCurrency(event.amount)}
                </td>
                <td className={`px-4 py-3 text-sm text-right whitespace-nowrap ${
                  (event.running_balance || 0) >= 0 ? 'text-gray-900' : 'text-red-600'
                }`}>
                  {formatCurrency(event.running_balance || 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Show more button */}
      {filteredEvents.length > 10 && (
        <div className="px-4 py-3 border-t text-center">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {showDetails
              ? 'Mostra meno'
              : `Mostra tutti (${filteredEvents.length - 10} altri)`
            }
          </button>
        </div>
      )}
    </div>
  );
}
